import { createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import {
  createGenericSlice,
  createReducers,
  ExtraReducers,
  GenericState,
} from 'stores/generics'
import { Payload } from 'stores'
import {
  CheckoutInstallmentOption,
  PurchaseIntentionDataError,
  PurchaseIntentionResponse,
} from '@types'
import {
  STORE_STATUS_INITIALIZED,
  STORE_CHECKOUT_PAYMENT,
  STORE_CHECKOUT_PURCHASE_INTENTION,
} from 'constants/store'
import { PaymentOption, PurchaseIntentionStatus } from 'constants/enums'

export type PurchaseIntentionState = {
  detail: PurchaseIntentionResponse
  selectedPlan: string | null
  selectedInstallment: CheckoutInstallmentOption | null
  selectedPayment: PaymentOption | null
  lastOrder: {
    chosenPaymentOption: number[]
  } | null
  lastOrderChecked: boolean
}

type State = GenericState<PurchaseIntentionState, PurchaseIntentionDataError>

export const initialState: State = {
  data: {
    detail: {
      id: '',
      supplier_name: '',
      supplier_company_id: '',
      merchant_company_id: '',
      merchant_document_number: '',
      email: '',
      url_callback: '',
      external_id: '',
      amount_cents: 0,
      payment_options: {},
      origin: 'sdk',
      created_at: '',
      discount_amount_cents: 0,
      next_business_day: '',
      has_pending_otp_verify: false,
      banner: {},
      status: PurchaseIntentionStatus.Pending,
    },
    selectedInstallment: null,
    selectedPlan: null,
    selectedPayment: PaymentOption.RESERVATION,
    lastOrder: null,
    lastOrderChecked: false,
  },
  status: STORE_STATUS_INITIALIZED,
  error: null,
}

function setDataByPurchaseIntention(response: PurchaseIntentionResponse) {
  if (response.is_new_payment_options_layout) {
    const firstPlan = Object.keys(response.payment_options)[0] ?? null
    const lastInstallment = firstPlan
      ? response.payment_options[firstPlan]?.at(-1)
      : null

    return {
      detail: response,
      selectedPlan: firstPlan,
      selectedInstallment: lastInstallment ?? null,
      selectedPayment: PaymentOption.RESERVATION,
    }
  }

  return {
    detail: response,
    selectedPlan: null,
    selectedInstallment: null,
    selectedPayment: PaymentOption.RESERVATION,
  }
}

export const fetchPurchaseIntention = createAsyncThunk<
  PurchaseIntentionState,
  Promise<PurchaseIntentionResponse>,
  Payload<PurchaseIntentionDataError, PurchaseIntentionResponse>
>(
  STORE_CHECKOUT_PURCHASE_INTENTION,
  async (promise: Promise<PurchaseIntentionResponse>, { rejectWithValue }) => {
    try {
      const response = await promise

      const data = setDataByPurchaseIntention(response)

      return {
        ...data,
        lastOrder: null,
        lastOrderChecked: false,
      }
    } catch (e: any) {
      return rejectWithValue({
        code: e.errorCode,
        message: e.error,
        details: e.data,
      })
    }
  }
)

const extraReducers: ExtraReducers = createReducers<
  PurchaseIntentionState,
  PurchaseIntentionResponse,
  Payload<PurchaseIntentionDataError, PurchaseIntentionResponse>
>(fetchPurchaseIntention, initialState.data)

const listSlice = createGenericSlice({
  name: STORE_CHECKOUT_PAYMENT,
  initialState,
  reducers: {
    setLastOrderChecked: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        data: { ...state.data, lastOrderChecked: action.payload },
      }
    },
    lastChosenPaymentOption: (state, action: PayloadAction<number[]>) => {
      return {
        ...state,
        data: {
          ...state.data,
          lastOrder: {
            chosenPaymentOption: action.payload,
          },
        },
      }
    },
    setPurchaseIntention: (
      state,
      action: PayloadAction<PurchaseIntentionResponse>
    ) => {
      const data = setDataByPurchaseIntention(action.payload)
      return {
        ...state,
        data: {
          ...state.data,
          ...data,
        },
      }
    },
    selectPlan: (state, action: PayloadAction<string | null>) => {
      return {
        ...state,
        data: {
          ...state.data,
          selectedPlan: action.payload,
        },
      }
    },
    selectInstallment: (
      state,
      action: PayloadAction<CheckoutInstallmentOption | null>
    ) => {
      return {
        ...state,
        data: {
          ...state.data,
          selectedInstallment: action.payload,
        },
      }
    },
    selectPayment: (state, action: PayloadAction<PaymentOption | null>) => {
      return {
        ...state,
        data: {
          ...state.data,
          selectedPayment: action.payload,
        },
      }
    },
    nextBusinessDay: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        data: {
          ...state.data,
          detail: {
            ...state.data.detail,
            next_business_day: action.payload,
          },
        },
      }
    },
    updateDetail: (state, action: PayloadAction<PurchaseIntentionResponse>) => {
      return {
        ...state,
        data: {
          ...state.data,
          detail: action.payload,
        },
      }
    },
  },
  extraReducers,
})

const { actions, reducer } = listSlice

const {
  clear,
  success,
  setLastOrderChecked,
  lastChosenPaymentOption,
  setPurchaseIntention,
  selectPlan,
  selectInstallment,
  selectPayment,
  nextBusinessDay,
  updateDetail,
} = actions

export {
  clear,
  success,
  setLastOrderChecked,
  lastChosenPaymentOption,
  setPurchaseIntention,
  selectPlan,
  selectInstallment,
  selectPayment,
  nextBusinessDay,
  updateDetail,
}

export default reducer
